"use client";

import { useEffect, useRef, useState } from "react";
import type { FormEvent } from "react";
import { useLocale, useTranslations } from "next-intl";
import { useSearchParams } from "next/navigation";
import { safeAuthNext } from "@/lib/auth-redirect";
import * as Sentry from "@sentry/nextjs";
import { Link } from "@/i18n/navigation";
import posthog from "posthog-js";
import { AuthCard } from "./auth-card";
import { AuthInput } from "./auth-input";
import { AuthButton } from "./auth-button";
import { AuthMethods } from "./auth-methods";
import { LegalFooter } from "./legal-footer";
import { EmailInUseNotice } from "./email-in-use-notice";
import { errorCode } from "./error-mapping";

export function SignInForm() {
  const t = useTranslations("auth");
  const locale = useLocale();
  const searchParams = useSearchParams();
  const nextPath = safeAuthNext(searchParams.get("next"), locale);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const noticeRef = useRef<HTMLDivElement | null>(null);

  const emailInUseProvider =
    errorKey === "errors.emailInUseGoogle"
      ? "google"
      : errorKey === "errors.emailInUseApple"
        ? "apple"
        : null;

  useEffect(() => {
    if (emailInUseProvider && noticeRef.current) {
      noticeRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [emailInUseProvider]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setLoading(true);
    setErrorKey(null);
    try {
      // Race the Next.js login route against a 20s timeout so a stuck
      // socket surfaces as an error instead of a permanent spinner.
      const result = (await Promise.race([
        fetch("/api/auth/email/login", {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ email: email.trim(), password }),
        }).then(async (res) => {
          const data = (await res.json()) as { ok?: boolean; code?: string };
          if (!res.ok || !data.ok) {
            throw new Error(data.code || "invalidCredentials");
          }
          return data;
        }),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error("loginWithPassword timeout")), 20_000),
        ),
      ])) as { ok: true };
      void result;
      posthog.capture("signed_in", { auth_provider: "email" });
      window.location.href = nextPath;
    } catch (err) {
      const code = errorCode(err);
      setErrorKey(code);
      posthog.capture("sign_in_failed", { auth_provider: "email", error_code: code });
      if (code === "errors.generic") {
        Sentry.captureException(err, { tags: { feature: "email_signin" } });
      }
      setLoading(false);
    }
  }

  return (
    <AuthCard
      title={t("signIn.title")}
      subtitle={
        <>
          {t("signIn.subtitleBefore")}{" "}
          <Link href={`/sign-up?next=${encodeURIComponent(nextPath)}`} className="font-medium text-ink underline underline-offset-4 decoration-ink/30 hover:decoration-ink">
            {t("signIn.subtitleLink")}
          </Link>
          .
        </>
      }
      footer={<LegalFooter />}
    >
      {emailInUseProvider && (
        <div ref={noticeRef}>
          <EmailInUseNotice provider={emailInUseProvider} email={email.trim().toLowerCase()} />
        </div>
      )}

      <AuthMethods source="sign_in" />

      <form onSubmit={onSubmit} className="flex flex-col gap-4">
        <AuthInput
          label={t("signIn.emailLabel")}
          name="email"
          type="email"
          autoComplete="email"
          required
          placeholder={t("signIn.emailPlaceholder")}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <div className="flex flex-col gap-1.5">
          <AuthInput
            label={t("signIn.passwordLabel")}
            name="password"
            type="password"
            autoComplete="current-password"
            required
            placeholder={t("signIn.passwordPlaceholder")}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <Link
            href="/forgot-password"
            className="self-end text-[13px] font-medium text-ink-mute underline underline-offset-4 decoration-ink/20 hover:text-ink hover:decoration-ink"
          >
            {t("signIn.forgot")}
          </Link>
        </div>
        {errorKey && !emailInUseProvider && (
          <p className="text-sm text-red-400 text-center">{t(errorKey)}</p>
        )}
        <AuthButton type="submit" loading={loading} className="mt-2">
          {t("signIn.submit")}
        </AuthButton>
      </form>
    </AuthCard>
  );
}
